import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import SEO from "../components/SEO";
import logisticsImg from "../assets/images/authentic/wool_transport_logistics.webp";
import fleeceStockImg from "../assets/images/authentic/raw_wool_fleece_stock.webp";
import {
  MapPin,
  CheckCircle2,
  ArrowRight,
  Phone,
  MessageCircle,
  Building2,
  Truck,
  Compass,
  HelpCircle,
} from "lucide-react";

const WoolSupplyRajasthan = () => {
  const { lPath, isRTL } = useLanguage();

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://skwoolens.trivanahospitality.com/en"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Wool Supply in Rajasthan",
        "item": "https://skwoolens.trivanahospitality.com/en/wool-supply-rajasthan"
      }
    ]
  };

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Raw Wool Trading & Supply in Rajasthan",
    "provider": {
      "@type": "WholesaleStore",
      "name": "S.K. Woollens",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "B-26, Un Mandi, Antyodaya Nagar",
        "addressLocality": "Bikaner",
        "addressRegion": "Rajasthan",
        "postalCode": "334001",
        "addressCountry": "IN"
      },
      "telephone": "+91-7014581250"
    },
    "description": "Rajasthan-wide raw wool sourcing, carpet-grade wool supply, and mandi trading connections based in Bikaner. Supplying greasy and scoured bales to spinning mills across India.",
    "areaServed": "Rajasthan, India",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Rajasthan Raw Wool Procurement & Bulk Supply"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Why is Rajasthan the premier hub for carpet-grade raw wool in India?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Rajasthan produces a major portion of India's indigenous raw wool. Indigenous sheep breeds adapted to arid grazing produce resilient, high-bulk fleece (primarily 30+ and 32–34+ micron) that is globally recognized for superior springiness and dye absorption in carpet yarn."
        }
      },
      {
        "@type": "Question",
        "name": "How does S.K. Woollens handle wool procurement across Rajasthan?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "From our operational hub at B-26 Un Mandi, Bikaner, we inspect, sample, and consolidate fleece arrivals from major shearing belts across Western and Northern Rajasthan. Our established networks enable consistent lot selection, clean yield testing, and buffer storage."
        }
      },
      {
        "@type": "Question",
        "name": "Can spinning mills outside Rajasthan procure raw wool from S.K. Woollens?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. We regularly supply spinning mills and yarn manufacturers across major carpet and textile belts including Bhadohi, Mirzapur, Panipat, Amritsar, and Jaipur with verified truckload shipments in both greasy and scoured condition."
        }
      },
      {
        "@type": "Question",
        "name": "What commercial specifications are required to initiate an enquiry?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Buyers should share target micron (e.g., 30+, 32–34+), staple length requirement (e.g., 50–75mm or 75–100mm+), vegetable matter limits, yield expectations, and monthly or quarterly tonnage requirements."
        }
      }
    ]
  };

  const schema = [breadcrumbSchema, serviceSchema, faqSchema];

  return (
    <div className="space-y-16 sm:space-y-24 py-6">
      <SEO
        title="Wool Supplier in Rajasthan | S.K. Woollens — Raw Wool Trading Hub"
        description="Looking for an established wool supplier in Rajasthan? S.K. Woollens supplies carpet-grade raw wool (30+ & 32-34+ micron) from Bikaner Un Mandi to spinning mills across India."
        keywords="wool supplier in Rajasthan, raw wool trader in Rajasthan, wool agent in Rajasthan India, carpet wool supplier in Rajasthan, bulk raw wool supplier Rajasthan, wool trading company Rajasthan, Bikaner wool market supplier, Rajasthan carpet grade wool trader"
        canonicalUrl="https://skwoolens.trivanahospitality.com/en/wool-supply-rajasthan"
        schema={schema}
      />

      {/* 1. Header Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/50 via-white to-wool-sand/30 border border-wool-border rounded-3xl p-8 sm:p-14 shadow-subtle">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="wool-badge inline-flex">
                <Compass className="w-3.5 h-3.5 text-wool-bronze" />
                <span>Rajasthan Wool Trade Corridor | Bikaner Mandi Hub</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-wool-charcoal tracking-tight font-display">
                Wool Supplier & Raw Wool Trading in Rajasthan
              </h1>
              <p className="text-base sm:text-lg text-wool-slate leading-relaxed">
                Rajasthan stands as the cornerstone of India's indigenous wool industry. From our base in Bikaner's Un Mandi, S.K. Woollens provides commercial spinning mills and carpet yarn manufacturers with reliable sourcing, grading, and bulk supply of Rajasthan carpet-grade fleece.
              </p>

              <div className="p-4 bg-white/80 border border-wool-border rounded-xl text-xs sm:text-sm text-wool-slate">
                <span className="font-semibold text-wool-bronze block mb-1">
                  1965 Mandi Legacy — Care of Bulakichand Kesharichand:
                </span>
                Carrying forward six decades of wool trading expertise, combining deep pastoralist sourcing networks across Rajasthan with dependable B2B commercial fulfillment.
              </div>

              <div className="pt-2 flex flex-wrap gap-4">
                <a
                  href="https://wa.me/917014581250?text=Hi%2C%20I%20am%20enquiring%20about%20raw%20wool%20supply%20from%20Rajasthan"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-300" />
                  <span>Enquire on WhatsApp</span>
                </a>
                <Link
                  to={lPath("/contact")}
                  className="btn-secondary inline-flex items-center gap-2"
                >
                  <span>Contact Our Trading Desk</span>
                  <ArrowRight className={`w-4 h-4 ${isRTL ? "rotate-180" : ""}`} />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-2xl overflow-hidden border border-wool-border shadow-premium relative">
                <img
                  src={logisticsImg}
                  alt="S.K. Woollens transport logistics and bulk wool dispatch across Rajasthan"
                  className="w-full h-80 object-cover"
                  loading="eager"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-wool-charcoal/80 via-transparent to-transparent flex items-end p-5">
                  <div className="text-white text-xs space-y-1">
                    <p className="font-bold text-sm">Rajasthan Inter-State Dispatch</p>
                    <p className="text-wool-sand/80">Direct freight connections from Bikaner Mandi to industrial spinning hubs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Rajasthan Sourcing Belts & Infrastructure */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="space-y-2 text-start">
            <span className="text-xs font-bold uppercase tracking-widest text-wool-bronze">
              Regional Provenance
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Rajasthan's Pastoral Belts & The Bikaner Advantage
            </h2>
            <p className="text-sm sm:text-base text-wool-slate max-w-3xl">
              Rajasthan accounts for the lion's share of India's carpet-grade wool production. Arid grazing conditions and indigenous sheep breeds produce wool fibers with distinctive bulk, high tensile resilience, and exceptional natural crimp.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border border-wool-border rounded-2xl p-6 space-y-3 shadow-subtle">
              <div className="w-10 h-10 rounded-xl bg-wool-sand/60 flex items-center justify-center text-wool-bronze">
                <Compass className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-wool-charcoal">
                Western Rajasthan Shearing Belts
              </h3>
              <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                Raw fleece sourced from Bikaner, Jaisalmer, Nagaur, and Marwar. These tracts produce robust white and light-tinged fleece renowned for resilience in spun yarns.
              </p>
            </div>

            <div className="bg-white border border-wool-border rounded-2xl p-6 space-y-3 shadow-subtle">
              <div className="w-10 h-10 rounded-xl bg-wool-sand/60 flex items-center justify-center text-wool-bronze">
                <Building2 className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-wool-charcoal">
                B-26 Un Mandi Aggregation Base
              </h3>
              <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                Our central premises inside Asia's largest wool market allow direct daily inspection of farm arrivals, certified weighment, lot segregation, and buffer godown storage.
              </p>
            </div>

            <div className="bg-white border border-wool-border rounded-2xl p-6 space-y-3 shadow-subtle">
              <div className="w-10 h-10 rounded-xl bg-wool-sand/60 flex items-center justify-center text-wool-bronze">
                <Truck className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-bold text-wool-charcoal">
                Statewide Scouring & Transport
              </h3>
              <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                Seamless coordination with local scouring plants for clean yield delivery, coupled with reliable dedicated truckload logistics to mills across India.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Wool Specifications Handled in Rajasthan */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-wool-charcoal text-white rounded-3xl p-8 sm:p-12 shadow-premium">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-6 space-y-4">
              <span className="text-xs font-bold uppercase tracking-widest text-wool-sand/80">
                Specification-Based Supply
              </span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
                Carpet-Grade Wool Specifications Traded
              </h2>
              <p className="text-sm text-wool-sand/80 leading-relaxed">
                Spinning mills require precise fibre metrics to maintain count consistency and avoid spinning breaks. S.K. Woollens supplies wool graded according to verifiable commercial standards:
              </p>

              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-white">30+ & 32–34+ Micron Carpet Fleece</h4>
                    <p className="text-xs text-wool-sand/70">Coarse, resilient staple ideal for woollen-spun carpet yarns with high pile recovery.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-white">Staple Length: 50mm to 100mm+</h4>
                    <p className="text-xs text-wool-sand/70">Carefully sorted for fibre length consistency required by high-speed carding machines.</p>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-3 bg-white/5 border border-white/10 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-bold text-white">Greasy & Scoured Bales</h4>
                    <p className="text-xs text-wool-sand/70">Supplied in raw unwashed farm lots or commercially scoured bales with low residual vegetable matter.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-2xl overflow-hidden border border-white/10 shadow-lg">
                <img
                  src={fleeceStockImg}
                  alt="Rajasthan raw wool fleece stock graded at S.K. Woollens Bikaner godowns"
                  className="w-full h-80 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4. Serving Spinning Mills Across India */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white border border-wool-border rounded-3xl p-8 sm:p-12 shadow-subtle space-y-6">
          <div className="space-y-2 text-start">
            <span className="text-xs font-bold uppercase tracking-widest text-wool-bronze">
              Industrial Mill Supply
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Supplying Major Textile & Carpet Clusters from Rajasthan
            </h2>
            <p className="text-sm sm:text-base text-wool-slate max-w-3xl">
              From our Bikaner operations, we maintain established commercial supply channels to leading spinning and weaving centers across India:
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { name: "Bhadohi (UP)", role: "Carpet Hand-Knotted Hub" },
              { name: "Mirzapur (UP)", role: "Tufted & Flatweave Mills" },
              { name: "Panipat (HR)", role: "Shoddy & Woollen Spinning" },
              { name: "Amritsar (PB)", role: "Worsted & Blended Spinning" },
              { name: "Jaipur (RJ)", role: "Handloom & Craft Carpets" },
              { name: "Bikaner (RJ)", role: "Local Mandi & Carding Hub" },
            ].map((cluster, i) => (
              <div key={i} className="p-4 bg-wool-sand/30 border border-wool-border/70 rounded-xl text-center space-y-1">
                <p className="font-bold text-xs sm:text-sm text-wool-charcoal">{cluster.name}</p>
                <p className="text-[11px] text-wool-muted">{cluster.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. FAQs on Rajasthan Wool Supply */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="space-y-2 text-start">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-wool-bronze">
              <HelpCircle className="w-4 h-4" />
              <span>Frequently Asked Questions</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Questions on Sourcing Wool from Rajasthan
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-wool-border rounded-2xl p-6 space-y-2 shadow-subtle">
              <h3 className="text-base font-bold text-wool-charcoal">
                Why is Rajasthan the premier hub for carpet-grade raw wool?
              </h3>
              <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                Rajasthan produces a major portion of India's indigenous raw wool. Indigenous sheep breeds adapted to arid grazing produce resilient, high-bulk fleece (primarily 30+ and 32–34+ micron) that is globally recognized for superior springiness and dye absorption in carpet yarn.
              </p>
            </div>

            <div className="bg-white border border-wool-border rounded-2xl p-6 space-y-2 shadow-subtle">
              <h3 className="text-base font-bold text-wool-charcoal">
                How does S.K. Woollens handle wool procurement across Rajasthan?
              </h3>
              <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                From our operational hub at B-26 Un Mandi, Bikaner, we inspect, sample, and consolidate fleece arrivals from major shearing belts across Western and Northern Rajasthan. Our established networks enable consistent lot selection, clean yield testing, and buffer storage.
              </p>
            </div>

            <div className="bg-white border border-wool-border rounded-2xl p-6 space-y-2 shadow-subtle">
              <h3 className="text-base font-bold text-wool-charcoal">
                Can spinning mills outside Rajasthan procure raw wool from S.K. Woollens?
              </h3>
              <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                Yes. We regularly supply spinning mills and yarn manufacturers across major carpet and textile belts including Bhadohi, Mirzapur, Panipat, Amritsar, and Jaipur with verified truckload shipments in both greasy and scoured condition.
              </p>
            </div>

            <div className="bg-white border border-wool-border rounded-2xl p-6 space-y-2 shadow-subtle">
              <h3 className="text-base font-bold text-wool-charcoal">
                What commercial specifications are required to initiate an enquiry?
              </h3>
              <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
                Buyers should share target micron (e.g., 30+, 32–34+), staple length requirement (e.g., 50–75mm or 75–100mm+), vegetable matter limits, yield expectations, and monthly or quarterly tonnage requirements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Contact & Enquiry CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-wool-sand/60 via-white to-wool-sand/40 border border-wool-border rounded-3xl p-8 sm:p-12 shadow-subtle text-center space-y-6">
          <div className="max-w-2xl mx-auto space-y-2">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-wool-charcoal font-display">
              Connect with Our Rajasthan Trading Desk
            </h2>
            <p className="text-xs sm:text-sm text-wool-slate leading-relaxed">
              Whether you require spot mandi arrivals or multi-tonne contracts for carpet yarn spinning, our team in Bikaner is ready to discuss your raw wool requirements.
            </p>
          </div>

          <div className="flex flex-wrap justify-center items-center gap-4">
            <a
              href="https://wa.me/917014581250?text=Hi%2C%20I%20would%20like%20to%20discuss%20raw%20wool%20supply%20from%20Rajasthan"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
            >
              <MessageCircle className="w-4 h-4 text-emerald-300" />
              <span>Chat with Nikhil Banthia (+91 7014581250)</span>
            </a>
            <a
              href="tel:919829109294"
              className="btn-secondary inline-flex items-center gap-2"
            >
              <Phone className="w-4 h-4 text-wool-bronze" />
              <span>Call Sunil Banthia (+91 9829109294)</span>
            </a>
          </div>

          <div className="text-xs text-wool-muted flex items-center justify-center gap-1.5 pt-2">
            <MapPin className="w-3.5 h-3.5 text-wool-bronze shrink-0" />
            <span>Premises: B-26, Un Mandi, Antyodaya Nagar, Bikaner, Rajasthan 334001, India</span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WoolSupplyRajasthan;
